// ============================================================================
// User Dashboard (Viewer Role)
// Feature: 136-dashboard-alignment (T031-T035)
// Created: 2026-01-22
// Updated: 2026-02-17 - Replaced mock data with live API calls (Constitution XXII)
// Description: Compact, information-dense workspace view for end users
// ============================================================================

// Updated: 2026-05-31 — Spec 160 T023: client-scoped KPI panels (read-only, no bell)
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { CuFleetPanel } from '../../components/Dashboard/panels/CuFleetPanel';
import { StoragePanel } from '../../components/Dashboard/panels/StoragePanel';
import { GovernancePanel } from '../../components/Dashboard/panels/GovernancePanel';
import { EntityGrowthPanel } from '../../components/Dashboard/panels/EntityGrowthPanel';
import { apiClient } from '../../services/apiClient';
import type { DashboardKpis } from '../../../../shared/types/dashboard';

import { LayoutDashboard } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useHasAccess } from '../../hooks/useAccess';
import { PageHelpPanel } from '../../components/ui/PageHelpPanel';
import { PageLayout, DashboardPanel } from '../../components/ui';
import { useScreenRefresh } from '../../hooks/useScreenRefresh';
import { lastCollectedAt } from '../../utils/lastCollectedAt';
import { viewerDashboardHelp } from '../../data/help/viewerDashboardHelp';
import { WidgetSkeleton } from '../../components/Dashboard/widgets/WidgetSkeleton';
import { WidgetErrorState } from '../../components/Dashboard/widgets/WidgetErrorState';
import { Extended } from '../../components/layout/Responsive';

interface UserWorkspace {
  id: string;
  name: string;
  description: string;
  type: string;
  status: 'active' | 'inactive' | 'pending';
  lastAccessed: string;
  lakehouses: number;
  isFavorite: boolean;
}

interface QuickLink {
  id: string;
  label: string;
  path: string;
  icon: string;
  description: string;
}


const UserDashboard: React.FC = () => {
  const { user } = useAuth();

  // Icon renderer for quick links
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'folder':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />;
      case 'database':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />;
      case 'clock':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'book':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />;
      default:
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />;
    }
  };

  // Recent workspaces — real data from /api/workspaces sorted by updated_at
  const workspacesQuery = useQuery<UserWorkspace[]>({
    queryKey: ['user', 'dashboard', 'workspaces'],
    queryFn: async () => {
      const res = await apiClient.get<{ workspaces?: any[]; data?: any[] }>('/api/workspaces?limit=5&sort=updated_at&order=desc');
      const rows = res.data?.workspaces ?? res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
      return rows.slice(0, 5).map((w: any) => ({
        id:           String(w.id ?? w.fabric_id ?? ''),
        name:         String(w.display_name ?? w.name ?? 'Workspace'),
        description:  String(w.description ?? ''),
        type:         String(w.workspace_type ?? w.type ?? ''),
        status:       w.is_active ? 'active' as const : 'inactive' as const,
        lastAccessed: String(w.updated_at ?? w.last_activity ?? ''),
        lakehouses:   Number(w.lakehouse_count ?? 0),
        isFavorite:   true,
      }));
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Activity feed — /api/dashboards/activity (real multi-source feed)
  const activityQuery = useQuery<Array<{ id: string; type: string; severity: string; title: string; detail: string | null; timestamp: string }>>({
    queryKey: ['user', 'dashboard', 'activity'],
    queryFn: async () => {
      const res = await apiClient.get('/api/dashboards/activity?limit=10');
      return (res.data as any[]) ?? [];
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Spec 285: the Backup section is backup-licensed — no link to a page the user cannot open.
  const canSeeBackup = useHasAccess('backup');
  const quickLinks: QuickLink[] = [
    { id: '1', label: 'View Workspaces', path: '/workspaces',  icon: 'folder',   description: 'Browse your workspaces' },
    { id: '2', label: 'View Lakehouses', path: '/lakehouses',  icon: 'database', description: 'Explore data assets' },
    ...(canSeeBackup
      ? [{ id: '3', label: 'Backup History',  path: '/backup', icon: 'clock',    description: 'View backup records' }]
      : []),
    { id: '4', label: 'Documentation',   path: '/help/docs',   icon: 'book',     description: 'Read the user guide' },
  ];

  const favoriteWorkspaces = workspacesQuery.data ?? [];

  const kpisQuery = useQuery<DashboardKpis>({
    queryKey: ['dashboard', 'kpis', 'Viewer'],
    queryFn: async () => (await apiClient.get<DashboardKpis>('/api/dashboards/kpis')).data,
    staleTime: 55_000, refetchInterval: 60_000, refetchOnWindowFocus: false,
  });
  const kpis = kpisQuery.data; const kpisLoading = kpisQuery.isLoading;
  // Spec 226: the panels poll for themselves (owner decision 10); the header Refresh re-fetches them all.
  const pageRefresh = useScreenRefresh();

  return (
    <PageLayout
      title={`Welcome, ${user?.displayName?.split(' ')[0] || 'User'}`}
      description="Your workspace overview"
      icon={<LayoutDashboard className="h-6 w-6" />}
      actions={<PageHelpPanel content={viewerDashboardHelp} buttonSize="sm" />}
      onRefresh={pageRefresh.refresh}
      isRefreshing={pageRefresh.isRefreshing}
      lastFetchedAt={kpisQuery.dataUpdatedAt}
      collectedAt={lastCollectedAt(kpis?.collectionHealth)}
      refreshError={pageRefresh.error ?? kpisQuery.error}
    >
      {/* Spec 160 T023: client-scoped KPI panels — read-only (no bell, no acknowledge) */}
      <div className="mb-[var(--space-section)]">
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <CuFleetPanel data={kpis?.cuFleet} loading={kpisLoading} />
          <StoragePanel data={kpis?.storage} loading={kpisLoading} showCostEstimate={false} />
          <GovernancePanel data={kpis?.governance} loading={kpisLoading} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          <EntityGrowthPanel label="Workspaces" data={kpis?.entities?.workspaces} loading={kpisLoading} />
          <EntityGrowthPanel label="Lakehouses" data={kpis?.entities?.lakehouses} loading={kpisLoading} color="#0ea5e9" />
          <EntityGrowthPanel label="Warehouses" data={kpis?.entities?.warehouses} loading={kpisLoading} color="#f59e0b" />
          <EntityGrowthPanel label="Container Apps" data={kpis?.entities?.containerApps} loading={kpisLoading} color="#10b981" />
        </div>
      </div>

      {/* Compact Grid Layout - 4 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Favorite Workspaces - 2 columns — T035: zero/loading/error states */}
        <div className="lg:col-span-2">
          <DashboardPanel
            title={<span className="flex items-center gap-1.5"><span className="text-yellow-500">⭐</span> Recent Workspaces</span>}
            actions={
              <Link to="/workspaces" className="text-xs font-medium text-[var(--color-accent)] hover:underline">
                View All →
              </Link>
            }
          >
            {workspacesQuery.isLoading ? (
              <WidgetSkeleton rows={3} />
            ) : workspacesQuery.isError ? (
              <WidgetErrorState
                message="Failed to load workspaces"
                onRetry={() => workspacesQuery.refetch()}
              />
            ) : favoriteWorkspaces.length === 0 ? (
              <div className="py-6 text-center text-[var(--color-text-secondary)]">
                <svg className="mx-auto mb-2 h-10 w-10 text-[var(--color-text-secondary)] opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">No recent workspaces</p>
                <p className="mt-1 text-xs">Your recently accessed workspaces will appear here.</p>
                <Link to="/workspaces" className="mt-2 inline-block text-xs text-[var(--color-accent)] hover:underline">
                  Browse Workspaces →
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {favoriteWorkspaces.map((workspace) => (
                <Link
                  key={workspace.id}
                  to={`/workspaces/${workspace.id}`}
                  className="block rounded bg-[var(--color-bg-subtle)] p-2.5 transition-colors hover:bg-[var(--color-border)]"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="truncate text-sm font-medium text-[var(--color-text-primary)]">{workspace.name}</h4>
                        <span className={clsx(
                          'flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium',
                          workspace.status === 'active' && 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
                          workspace.status === 'inactive' && 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                        )}>
                          {workspace.status}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                        <span>{workspace.lakehouses} lakehouses</span>
                        <span>•</span>
                        <span>{workspace.lastAccessed}</span>
                      </div>
                    </div>
                  </div>
                </Link>
                ))}
              </div>
            )}
          </DashboardPanel>
        </div>

        {/* Quick Actions - Compact */}
        <div className="lg:col-span-2">
          <DashboardPanel title="Quick Actions">
            <div className="space-y-1.5">
              {quickLinks.map((link) => (
                <Link
                  key={link.id}
                  to={link.path}
                  className="group flex items-center gap-2 rounded p-2 transition-colors hover:bg-[var(--color-bg-subtle)]"
                >
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-blue-50 group-hover:bg-blue-100 dark:bg-blue-900/30 dark:group-hover:bg-blue-900/50">
                    <svg className="h-4 w-4 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {renderIcon(link.icon)}
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-[var(--color-text-secondary)] group-hover:text-[var(--color-text-primary)]">{link.label}</span>
                </Link>
              ))}
            </div>
          </DashboardPanel>
        </div>


        {/* Recent Activity — full-width, same real feed as Admin.
            Spec 197 US4: extended activity feed hidden on mobile (quick glance =
            Portfolio Overview + Recent Workspaces + Quick Actions). */}
        <Extended>
        <div className="lg:col-span-4">
          <DashboardPanel title="Recent Activity">
            {activityQuery.isLoading ? (
              <WidgetSkeleton rows={5} />
            ) : activityQuery.isError ? (
              <WidgetErrorState message="Failed to load activity" onRetry={() => activityQuery.refetch()} />
            ) : !activityQuery.data?.length ? (
              <p className="py-6 text-center text-sm text-[var(--color-text-secondary)]">No activity in the last 48 hours</p>
            ) : (
              <div>
                {activityQuery.data.map((item) => {
                  const dotColor = item.severity === 'success' ? 'bg-green-500' : item.severity === 'error' ? 'bg-red-500' : item.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500';
                  const ageMs = Date.now() - new Date(item.timestamp).getTime();
                  const ago = Math.floor(ageMs / 3600000) >= 1
                    ? `${Math.floor(ageMs / 3600000)}h ago`
                    : `${Math.floor(ageMs / 60000)}m ago`;
                  return (
                    <div key={item.id} className="flex items-start gap-3 border-b border-[var(--color-border)] py-2 last:border-0">
                      <div className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${dotColor}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[var(--color-text-primary)]">{item.title}</p>
                        {item.detail && <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{item.detail}</p>}
                      </div>
                      <span className="mt-0.5 flex-shrink-0 text-xs text-[var(--color-text-secondary)]">{ago}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </DashboardPanel>
        </div>
        </Extended>
      </div>
    </PageLayout>
  );
};

export default UserDashboard;
